import React, { useEffect, useState } from "react";
import {
  CZECH_MONTHS,
  CZECH_WEEKDAYS,
  formatCzechDate,
  getCalendarDays,
  isSameDay,
  parseCzechDate,
} from "../utils/date-utils.ts";

interface DatePickerModalProps {
  /** Aktuálně vybrané datum ve tvaru DD.MM.RRRR. */
  value: string;
  onSelect: (date: string) => void;
  onClose: () => void;
}

const DatePickerModal: React.FC<DatePickerModalProps> = ({
  value,
  onSelect,
  onClose,
}) => {
  const today = new Date();
  const selected = parseCzechDate(value);

  // Měsíc zobrazený v kalendáři (ne nutně ten s vybraným dnem)
  const [viewDate, setViewDate] = useState<Date>(() => {
    const base = selected || today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const days = getCalendarDays(year, month);

  const changeMonth = (delta: number) => {
    setViewDate(new Date(year, month + delta, 1));
  };

  const goToToday = () => {
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
    onSelect(formatCzechDate(today));
  };

  return (
    <div className="fixed inset-0 z-20 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block w-full max-w-sm p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex justify-between items-center pb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Datum turnaje
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Zavřít</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Přepínání měsíců */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => changeMonth(-1)}
              title="Předchozí měsíc"
              className="px-3 py-1 rounded-md text-gray-600 hover:bg-gray-100"
            >
              ‹
            </button>
            <span className="font-medium text-gray-800">
              {CZECH_MONTHS[month]} {year}
            </span>
            <button
              onClick={() => changeMonth(1)}
              title="Následující měsíc"
              className="px-3 py-1 rounded-md text-gray-600 hover:bg-gray-100"
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {CZECH_WEEKDAYS.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-medium text-gray-500 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const isCurrentMonth = day.getMonth() === month;
              const isSelected = selected ? isSameDay(day, selected) : false;
              const isToday = isSameDay(day, today);

              return (
                <button
                  key={day.getTime()}
                  onClick={() => onSelect(formatCzechDate(day))}
                  className={`h-9 rounded-md text-sm ${
                    isSelected
                      ? "bg-blue-600 text-white font-semibold"
                      : isCurrentMonth
                      ? "text-gray-800 hover:bg-blue-50"
                      : "text-gray-400 hover:bg-gray-100"
                  } ${
                    isToday && !isSelected ? "ring-1 ring-blue-400" : ""
                  }`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
            >
              Dnes
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 focus:outline-none"
            >
              Zrušit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatePickerModal;
